/**
 * MarathiAkshata3d — interactive 3D Marathi wedding (akshata ceremony).
 *
 * Guests shower akshata on the couple, lower the antarpat, then see the
 * event details and RSVP. The experience component (and with it three.js,
 * the GLB and character textures) is code-split with React.lazy so no other
 * template pays for it.
 *
 * Registered under id 'marathi-akshata-3d':
 *   - /preview/marathi-akshata-3d and PublicInvitation render it via registry.get(templateId)
 *   - TemplateRenderer / DraftPreviewModal / StepTemplate full preview render it live
 *   - TemplateCardPreview shows the static poster via META.cardPosterUrl (no WebGL in thumbnails)
 *   - TemplateDirectPreviewPage reads query params via META.previewFromQueryParams
 */
import React, { Suspense, lazy } from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { MARATHI_AKSHATA_TEMPLATE_ID, POSTER_URL } from '@/src/modules/experiences/wedding/marathi-akshata/assets';

export const META: TemplateMetadata = {
  id: MARATHI_AKSHATA_TEMPLATE_ID,
  name: 'Marathi Akshata 3D Wedding',
  occasionType: 'WEDDING',
  description:
    'An interactive 3D Marathi wedding: guests shower akshata on the couple, lower the antarpat for the first glance, then find your date, venue and RSVP.',
  isPremium: true,
  tags: ['wedding', 'marathi', 'traditional', '3d', 'interactive', 'animated', 'akshata', 'premium'],
  colorScheme: { primary: '#6B3FD4', accent: '#E8B64C', bg: '#2A1838' },
  // Catalog cards show the static poster (no WebGL per card); /preview reads query params.
  cardPosterUrl: POSTER_URL,
  previewFromQueryParams: true,
};

const Experience = lazy(() => import('@/src/modules/experiences/wedding/marathi-akshata/Experience'));

function PosterFallback() {
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100dvh',
        minHeight: 560,
        background: `#2a1838 url(${POSTER_URL}) center / cover no-repeat`,
      }}
      aria-busy="true"
    />
  );
}

export function MarathiAkshata3d({ data, preview }: { data: InvitationData; preview?: boolean }) {
  return (
    <Suspense fallback={<PosterFallback />}>
      <Experience data={data} preview={preview} />
    </Suspense>
  );
}

registry.register({ metadata: META, component: MarathiAkshata3d });
